import { useEffect, useMemo, useState } from 'react';
import type { Address } from '@/types';
import { SearchableSelect, type SearchableOption } from './SearchableSelect';
import { US_STATES, isUnitedStates } from '@/lib/us-states';

interface AddressFieldsProps {
  value: Address;
  onChange: (next: Address) => void;
  labels: {
    address: string;
    address2: string;
    city: string;
    state: string;
    stateUs: string;
    country: string;
    postalCode: string;
  };
  language: 'ka' | 'en';
  inputClassName: string;
  labelClassName: string;
}

interface GpostCountry {
  CountryId: number;
  CountryNameEn: string;
  CountryNameGe: string;
}

// Fetched once per page rather than per address block — the profile renders two of these.
let countriesPromise: Promise<GpostCountry[]> | null = null;
function loadCountries(): Promise<GpostCountry[]> {
  if (!countriesPromise) {
    countriesPromise = fetch('/api/v1/shipping/countries')
      .then((r) => r.json())
      .then((res) => (res?.success && res.data?.Countries ? (res.data.Countries as GpostCountry[]) : []))
      .catch(() => []);
  }
  return countriesPromise;
}

/**
 * One address block: the same fields in the same order everywhere they are collected, so a
 * profile address and a checkout address can hold the same information.
 */
export function AddressFields({
  value,
  onChange,
  labels,
  language,
  inputClassName,
  labelClassName,
}: AddressFieldsProps) {
  const [countries, setCountries] = useState<GpostCountry[]>([]);

  useEffect(() => {
    let alive = true;
    void loadCountries().then((list) => {
      if (alive) setCountries(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  const set = (field: keyof Address, v: string): void => onChange({ ...value, [field]: v });

  const countryOptions: SearchableOption[] = useMemo(
    () => countries.map((c) => ({ value: c.CountryId, label: language === 'ka' ? c.CountryNameGe : c.CountryNameEn })),
    [countries, language],
  );

  const countryValue = useMemo(() => {
    const match = countries.find(
      (c) => c.CountryNameEn === value.country || c.CountryNameGe === value.country,
    );
    return match ? match.CountryId : null;
  }, [countries, value.country]);

  const isUs = isUnitedStates(value.country || '');
  const stateOptions: SearchableOption[] = useMemo(
    () => US_STATES.map((st, i) => ({ value: i, label: `${st.name} (${st.code})` })),
    [],
  );
  const stateValue = useMemo(() => {
    const idx = US_STATES.findIndex((st) => st.code === value.state);
    return idx >= 0 ? idx : null;
  }, [value.state]);

  return (
    <>
      <div>
        <label className={labelClassName}>{labels.address}</label>
        <input type="text" value={value.address} onChange={(e) => set('address', e.target.value)} className={inputClassName} />
      </div>
      <div>
        <label className={labelClassName}>{labels.address2}</label>
        <input type="text" value={value.address2 ?? ''} onChange={(e) => set('address2', e.target.value)} className={inputClassName} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClassName}>{labels.country}</label>
          <SearchableSelect
            options={countryOptions}
            value={countryValue}
            onChange={(_id, option) => {
              // Changing country invalidates a US state code, so clear it in the same update.
              onChange({ ...value, country: option.label, state: '' });
            }}
            placeholder={language === 'ka' ? 'აირჩიე ქვეყანა' : 'Select country'}
            emptyText={language === 'ka' ? 'ვერ მოიძებნა' : 'No matches'}
          />
        </div>
        <div>
          <label className={labelClassName}>{labels.city}</label>
          <input type="text" value={value.city} onChange={(e) => set('city', e.target.value)} className={inputClassName} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClassName}>{isUs ? labels.stateUs : labels.state}</label>
          {isUs ? (
            <SearchableSelect
              options={stateOptions}
              value={stateValue}
              onChange={(id) => set('state', US_STATES[id]?.code ?? '')}
              placeholder={language === 'ka' ? 'აირჩიე შტატი' : 'Select state'}
              emptyText={language === 'ka' ? 'ვერ მოიძებნა' : 'No matches'}
            />
          ) : (
            <input type="text" value={value.state ?? ''} onChange={(e) => set('state', e.target.value)} className={inputClassName} />
          )}
        </div>
        <div>
          <label className={labelClassName}>{labels.postalCode}</label>
          <input type="text" value={value.postalCode} onChange={(e) => set('postalCode', e.target.value)} className={inputClassName} />
        </div>
      </div>
    </>
  );
}
